/**
 * client/ui/roulette.ts
 * Responsabilidad: Controlador de la interfaz de usuario de la Ruleta Alquímica Diaria en el Modal de Cuenta.
 * Gestiona el temporizador de 24 horas, llamadas autoritativas REST al backend, alertas SweetAlert2 y sincronización de economía.
 */

import Swal from "sweetalert2";
import { RouletteWheel } from "../roulette";
import { AudioManager } from "../audio";
import { questManager } from "../quests";
import { safeGetStorage } from "../utils";

export class RouletteUI {
    private wheel: RouletteWheel | null = null;
    private audio: AudioManager;

    private modal = document.getElementById("roulette-modal");
    private closeBtn = document.getElementById("roulette-close");
    private canvas = document.getElementById("roulette-canvas") as HTMLCanvasElement | null;
    private timerLabel = document.getElementById("roulette-countdown-label");
    private timerDisplay = document.getElementById("roulette-countdown-timer");
    private spinBtn = document.getElementById("roulette-spin-btn") as HTMLButtonElement | null;
    private adBtn = document.getElementById("roulette-ad-btn") as HTMLButtonElement | null;

    private countdownTimer: NodeJS.Timeout | null = null;
    private msRemaining: number = 0;
    private isBusy: boolean = false;

    constructor(audio?: AudioManager) {
        this.audio = audio || new AudioManager();
        this.init();
    }

    private init(): void {
        if (this.canvas) {
            this.wheel = new RouletteWheel(this.canvas, this.audio);
        }

        this.closeBtn?.addEventListener("click", () => this.close());
        this.modal?.addEventListener("click", (e) => {
            if (e.target === this.modal) this.close();
        });
        window.addEventListener("alchem:open_roulette", () => this.open());
        window.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && this.modal && !this.modal.classList.contains("hidden")) {
                this.close();
            }
        });

        if (this.spinBtn) {
            this.spinBtn.addEventListener("click", () => this.handleSpinClick());
        }

        if (this.adBtn) {
            this.adBtn.disabled = true;
        }

        // Re-sincronizar cuando cambia la sesión
        window.addEventListener("alchem:auth_change", () => {
            void this.refresh();
        });
    }

    public open(): void {
        if (this.modal) {
            this.modal.classList.remove("hidden");
            void this.refresh();
        }
    }

    public close(): void {
        if (this.modal) {
            this.modal.classList.add("hidden");
        }
    }

    public getWheel(): RouletteWheel | null {
        return this.wheel;
    }

    /**
     * Consulta el estado de la ruleta desde /api/roulette/status y actualiza el botón / temporizador.
     */
    public async refresh(): Promise<void> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");

        let data: any = null;
        try {
            const headers: Record<string, string> = {};
            if (token) headers["Authorization"] = `Bearer ${token}`;

            const res = await fetch("/api/roulette/status", { headers });
            if (res.ok) {
                data = await res.json();
            }
        } catch (e) {
            console.warn("[RouletteUI] Error consultando /api/roulette/status:", e);
        }

        if (data && Array.isArray(data.segments) && this.wheel) {
            this.wheel.setSegments(data.segments);
        }

        if (!token || !data) {
            this.renderGuestState();
            this.updateBadges(false);
            return;
        }

        if (data.canSpinFree) {
            this.renderReadyState();
            this.updateBadges(true);
        } else {
            this.msRemaining = typeof data.msUntilNextFreeSpin === "number" ? data.msUntilNextFreeSpin : 0;
            this.startCountdown();
            this.updateBadges(false);
        }
    }

    private renderReadyState(): void {
        this.stopCountdown();
        if (this.timerLabel) {
            this.timerLabel.textContent = "Giro Diario Gratuito:";
        }
        if (this.timerDisplay) {
            this.timerDisplay.textContent = "¡DISPONIBLE AHORA!";
            this.timerDisplay.className = "roulette-timer ready";
        }
        if (this.spinBtn) {
            this.spinBtn.disabled = false;
            this.spinBtn.textContent = "🎡 ¡GIRAR RULETA GRATIS!";
            this.spinBtn.classList.add("pulse-ready");
        }
    }

    private renderGuestState(): void {
        this.stopCountdown();
        if (this.timerLabel) {
            this.timerLabel.textContent = "Estado de Cuenta:";
        }
        if (this.timerDisplay) {
            this.timerDisplay.textContent = "Inicia Sesión";
            this.timerDisplay.className = "roulette-timer";
        }
        if (this.spinBtn) {
            this.spinBtn.disabled = false;
            this.spinBtn.textContent = "🎡 ¡GIRAR RULETA GRATIS!";
            this.spinBtn.classList.add("pulse-ready");
        }
    }

    private startCountdown(): void {
        this.stopCountdown();

        const update = () => {
            if (this.msRemaining <= 0) {
                this.renderReadyState();
                this.updateBadges(true);
                return;
            }

            const totalSec = Math.floor(this.msRemaining / 1000);
            const hours = Math.floor(totalSec / 3600);
            const mins = Math.floor((totalSec % 3600) / 60);
            const secs = totalSec % 60;
            const timeStr = `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

            if (this.timerLabel) {
                this.timerLabel.textContent = "Próximo Giro Diario en:";
            }
            if (this.timerDisplay) {
                this.timerDisplay.textContent = timeStr;
                this.timerDisplay.className = "roulette-timer";
            }
            if (this.spinBtn && !this.isBusy) {
                this.spinBtn.disabled = true;
                this.spinBtn.textContent = `⏳ En espera (${timeStr})`;
                this.spinBtn.classList.remove("pulse-ready");
            }

            this.msRemaining -= 1000;
        };

        update();
        this.countdownTimer = setInterval(update, 1000);
    }

    private stopCountdown(): void {
        if (this.countdownTimer !== null) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
        }
    }

    /**
     * Muestra u oculta badges de notificación en botones de ruleta del menú.
     */
    private updateBadges(hasFreeSpin: boolean): void {
        const aliasRouletteBadge = document.getElementById("alias-roulette-badge");
        if (aliasRouletteBadge) {
            aliasRouletteBadge.classList.toggle("hidden", !hasFreeSpin);
        }

        const tabRoulette = document.getElementById("account-tab-roulette");
        if (tabRoulette) {
            tabRoulette.classList.toggle("has-badge", hasFreeSpin);
        }
    }

    /**
     * Manejador de click en el botón de giro.
     */
    private async handleSpinClick(): Promise<void> {
        if (this.isBusy || !this.wheel) return;

        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) {
            const result = await Swal.fire({
                title: '🎡 RULETA ALQUÍMICA',
                html: 'Para hacer girar la <b>Ruleta Diaria</b> y guardar tus recompensas de 💎 Cristales, 🔮 Orbes y el <b>Aura Fénix Legendaria 🌟</b>, necesitas iniciar sesión o registrarte gratis.<br><br>¿Deseas acceder a tu cuenta ahora?',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: '✨ Iniciar Sesión / Registrarme',
                cancelButtonText: 'Cerrar',
                confirmButtonColor: '#38bdf8',
                cancelButtonColor: '#64748b',
            });

            if (result.isConfirmed) {
                this.close();
                const accountModal = document.getElementById("account-modal");
                if (accountModal) {
                    accountModal.classList.remove("hidden");
                }
            }
            return;
        }

        this.isBusy = true;
        if (this.spinBtn) {
            this.spinBtn.disabled = true;
            this.spinBtn.textContent = "🔮 Invocando la Fortuna...";
            this.spinBtn.classList.remove("pulse-ready");
        }

        try {
            const res = await fetch("/api/roulette/spin", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = await res.json();
            if (!res.ok || !data.success) {
                this.isBusy = false;
                if (data.error === "cooldown_active") {
                    this.msRemaining = typeof data.msUntilNextFreeSpin === "number" ? data.msUntilNextFreeSpin : 0;
                    this.startCountdown();
                    await Swal.fire({
                        title: '⏳ Cooldown Activo',
                        text: 'Aún no han transcurrido 24 horas desde tu último giro diario.',
                        icon: 'warning',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#38bdf8',
                    });
                } else {
                    await Swal.fire({
                        title: 'Error',
                        text: data.error || 'No se pudo procesar el giro.',
                        icon: 'error',
                        confirmButtonText: 'Aceptar',
                    });
                    this.renderReadyState();
                }
                return;
            }

            // Iniciar animación en el Canvas de 5.5s con parada exacta en el índice devuelto por el backend
            const winningIndex = typeof data.winningIndex === "number" ? data.winningIndex : 0;
            await this.wheel.spinTo(winningIndex);

            // Sincronizar saldos de inmediato en la UI y questManager
            if (typeof data.crystals === "number" && typeof data.astralOrbs === "number") {
                const unlocked = data.segment?.type === "skin" && !data.compensated
                    ? [data.segment?.skinId || "aura:phoenix"]
                    : undefined;
                questManager.setBalances(data.crystals, data.astralOrbs, unlocked);
                window.dispatchEvent(new CustomEvent("alchem:auth_change", {
                    detail: { crystals: data.crystals, astralOrbs: data.astralOrbs }
                }));
            }

            // Reproducir fanfarria sonora
            try {
                this.audio.playSuccessSfx();
            } catch {
                // ignore
            }

            // Mostrar modal de recompensa
            if (data.compensated) {
                await Swal.fire({
                    title: '🌟 ¡SKIN REPETIDO!',
                    html: `¡La ruleta se detuvo en <b>${data.segment?.label || "Aura Fénix"}</b>!<br><br>Como ya la tienes desbloqueada, el Consejo Alquímico te compensa con:<br><br><span style="font-size: 26px; color: #a855f7; font-weight: 800;">+${data.compensationOrbs || 10} 🔮 Piedras Filosofales</span>`,
                    icon: 'info',
                    confirmButtonText: '✨ ¡Reclamar Compensación!',
                    confirmButtonColor: '#8b5cf6',
                });
            } else if (data.segment?.type === "skin") {
                await Swal.fire({
                    title: '🌟 ¡PREMIO LEGENDARIO!',
                    html: `¡FELICIDADES! ¡Has obtenido el codiciado <b>${data.segment?.label || "Aura Fénix"} 🌟</b> en la Ruleta Diaria!<br><br>Ya se encuentra equipable en tu vestidor alquímico.`,
                    icon: 'success',
                    confirmButtonText: '👑 ¡Fantástico!',
                    confirmButtonColor: '#f59e0b',
                });
            } else if (data.segment?.type === "crystals" || data.segment?.type === "orbs") {
                await Swal.fire({
                    title: '🎉 ¡PREMIO OBTENIDO!',
                    html: `¡Has ganado <b>${data.segment.label}</b> en la Ruleta Alquímica!`,
                    icon: 'success',
                    confirmButtonText: '¡Genial!',
                    confirmButtonColor: '#38bdf8',
                });
            } else {
                await Swal.fire({
                    title: '💨 ¡SIN PREMIO ESTA VEZ!',
                    text: 'La ruleta cayó en casillero vacío. ¡Vuelve en 24 horas por tu revancha gratuita!',
                    icon: 'info',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: '#64748b',
                });
            }

            // Iniciar cooldown de 24h
            this.msRemaining = typeof data.msUntilNextFreeSpin === "number" ? data.msUntilNextFreeSpin : 24 * 60 * 60 * 1000;
            this.startCountdown();
            this.updateBadges(false);

        } catch (e) {
            console.error("[RouletteUI] Error durante el giro de ruleta:", e);
            await Swal.fire({
                title: 'Error de Conexión',
                text: 'Hubo un problema al conectar con el servidor. Intenta de nuevo más tarde.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            this.renderReadyState();
        } finally {
            this.isBusy = false;
        }
    }

    public destroy(): void {
        this.stopCountdown();
        if (this.wheel) {
            this.wheel.destroy();
            this.wheel = null;
        }
    }
}
